/**
 * Swing fill after reload: checkbox selection is empty even when the same
 * row id is still in the store. A new row object is a refill; a new wrapper
 * around the same `_rowData` is not.
 */
export function checkboxSelectionAfterItemsReload<T>(
  _selection?: readonly T[] | null,
  _nextItems?: readonly T[] | null,
): T[] {
  return []
}

function backingRow(item: unknown): unknown {
  if (item && typeof item === 'object' && '_rowData' in item) {
    const row = (item as { _rowData?: unknown })._rowData
    if (row !== undefined) return row
  }
  return item
}

/** True when the view-model was rebuilt but the store rows are the same objects. */
export function isIdentityOnlyCheckboxItemsUpdate(
  previousItems?: readonly unknown[] | null,
  nextItems?: readonly unknown[] | null,
): boolean {
  if (!previousItems || !nextItems) return false
  if (previousItems.length === 0 || previousItems.length !== nextItems.length) return false
  for (let index = 0; index < previousItems.length; index += 1) {
    const previous = backingRow(previousItems[index])
    const next = backingRow(nextItems[index])
    if (previous == null || next == null || previous !== next) return false
  }
  return true
}

/**
 * Checkbox click rebuilds row wrappers. Keep the checked rows in that case.
 * A refill that replaces row objects still clears, including when `_id` matches.
 */
export function checkboxSelectionAfterItemsUpdate<T extends { __syntheticId?: string }>(
  current: readonly T[] | null | undefined,
  previousItems: readonly T[] | null | undefined,
  nextItems: readonly T[] | null | undefined,
): T[] {
  const selection = Array.isArray(current) ? current : []
  if (selection.length === 0) return []
  if (!nextItems || !isIdentityOnlyCheckboxItemsUpdate(previousItems, nextItems)) {
    return checkboxSelectionAfterItemsReload(selection, nextItems)
  }

  const bySyntheticId = new Map<string, T>()
  nextItems.forEach((item) => {
    if (item?.__syntheticId != null) bySyntheticId.set(String(item.__syntheticId), item)
  })

  const remapped: T[] = []
  for (const selected of selection) {
    const id = selected?.__syntheticId
    if (id == null) return checkboxSelectionAfterItemsReload(selection, nextItems)
    const match = bySyntheticId.get(String(id))
    if (!match) return checkboxSelectionAfterItemsReload(selection, nextItems)
    remapped.push(match)
  }
  return remapped
}
